import type {
  SortCountScanEventDTO,
  SortCountSubmissionPayloadDTO,
  SortCountTargetDTO,
  TempSortCountSessionDTO,
} from '../../types/sortAndCount';
import { buildDiffRows, buildSnapshot, buildSummary } from './core';

const normalizeCode = (value: any): string => String(value || '').trim().toUpperCase();

export const adaptTargetsFromStageData = (data: any): SortCountTargetDTO[] => {
  const rawTargets = Array.isArray(data?.targets)
    ? data.targets
    : (Array.isArray(data?.sort_count_targets) ? data.sort_count_targets : []);
  if (rawTargets.length > 0) {
    return rawTargets
      .map((item: any) => ({
        target_code: normalizeCode(item?.target_code || item?.code || item?.target_label),
        target_label: String(item?.target_label || item?.label || item?.target_code || item?.code || '').trim(),
        expected_qty: Math.max(0, Number(item?.expected_qty || item?.target_qty || 0)),
        sorting_mark: String(item?.sorting_mark || item?.product_sorting_code || '').trim() || undefined,
      }))
      .filter((item: SortCountTargetDTO) => !!item.target_code);
  }

  // 兜底兼容：若后端沿用 pick_rows，也可进入分拣清点逻辑
  const pickRows = Array.isArray(data?.pick_rows) ? data.pick_rows : [];
  return pickRows
    .map((row: any) => ({
      target_code: normalizeCode(row?.target_label || row?.target_code || row?.unit_code),
      target_label: String(row?.target_label || row?.target_code || row?.unit_code || '').trim(),
      expected_qty: Math.max(0, Number(row?.target_qty || row?.expected_qty || 0)),
      sorting_mark: String(row?.sorting_mark || row?.product_sorting_code || '').trim() || undefined,
    }))
    .filter((item: SortCountTargetDTO) => !!item.target_code);
};

export const adaptEventsFromMetrics = (metrics: Record<string, any>): SortCountScanEventDTO[] => {
  const events = Array.isArray(metrics?.sort_count_events)
    ? metrics.sort_count_events
    : (Array.isArray(metrics?.sort_and_count?.events) ? metrics.sort_and_count.events : []);
  return events
    .map((event: any) => ({
      id: String(event?.id || ''),
      scan_value: String(event?.scan_value || event?.scanValue || '').trim(),
      target_code: normalizeCode(event?.target_code || event?.targetCode),
      qty: Math.max(1, Number(event?.qty || 1)),
      source: (event?.source || 'scan'),
      container_code: String(event?.container_code || '').trim() || undefined,
      operated_at: String(event?.operated_at || event?.operatedAt || new Date().toISOString()),
    }))
    .filter((event: SortCountScanEventDTO) => !!event.id && !!event.target_code);
};

export const buildStageSubmissionPayload = (input: {
  stageNo?: number | null;
  idempotencyKey?: string;
  targets: SortCountTargetDTO[];
  events: SortCountScanEventDTO[];
}): SortCountSubmissionPayloadDTO => {
  const rows = buildDiffRows(input.targets, input.events);
  const summary = buildSummary(rows);
  const snapshot = buildSnapshot(input.targets, rows, input.events);
  return {
    stage_idx: Number.isFinite(Number(input.stageNo)) ? Number(input.stageNo) : null,
    idempotency_key: String(input.idempotencyKey || '').trim() || undefined,
    metrics: {
      sort_and_count: {
        rows,
        summary,
        events: input.events,
        snapshot,
      },
    },
  };
};

export const buildTempSessionPayload = (input: {
  id: string;
  name: string;
  context?: TempSortCountSessionDTO['context'];
  targets: SortCountTargetDTO[];
  events: SortCountScanEventDTO[];
}): TempSortCountSessionDTO => ({
  id: String(input.id || ''),
  name: String(input.name || '').trim() || '临时分拣清点会话',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  context: input.context,
  targets: input.targets,
  events: input.events,
});

